import React from 'react';

import {
  PageWithHeader,
  Content,
  InfoCard,
  LinkButton,
} from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { radius } from '../../themes/revenium';
import ExploreIcon from '@material-ui/icons/Explore';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import DescriptionIcon from '@material-ui/icons/Description';

const useStyles = makeStyles((theme: Theme) => ({
  introText: {
    marginBottom: theme.spacing(3),
  },
  cardIcon: {
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: '2rem',
  },
  // VIS-01 (gate finding) — a centered, evenly-filled 3-column grid. The old
  // ItemCardGrid uses `repeat(auto-fill, minmax(22em, 1fr))`, which leaves empty
  // trailing columns on wide screens (cards strand left-of-center). A fixed
  // 3-column grid fills the full content width evenly, and collapses to a single
  // column on small screens.
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridGap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  // VIS-01 / D-08 — equal-height getting-started cards. InfoCard variant
  // "gridItem" does not equalize row height on its own, so we make each card
  // fill the grid cell (height 100%) and lay its content out as a flex column.
  // The auto-margin on the CTA (below) then pushes buttons to a common baseline.
  gettingStartedCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  // Guaranteed breathing room above the CTA even on a short card. A margin on
  // the CTA's top would be swallowed by `marginTop: auto`, so we instead give
  // the description text a bottom margin — this survives the auto-margin and
  // does not distort the button's internal padding.
  cardBody: {
    marginBottom: theme.spacing(2),
  },
  cardCta: {
    // marginTop:auto bottom-aligns the CTA across cards of differing height so
    // buttons share a common baseline. The description's marginBottom (above)
    // provides the separation from the body copy.
    marginTop: 'auto',
    borderRadius: radius.md,
    alignSelf: 'flex-start',
  },
}));

export const HomePage = () => {
  const classes = useStyles();

  return (
    <PageWithHeader
      title="Indiana Digital Marketplace"
      subtitle="Indiana Office of Technology"
      themeId="home"
    >
      <Content>
        <Typography variant="body1" className={classes.introText}>
          The Indiana Digital Marketplace provides developers with access to State of
          Indiana APIs and services. Use this portal to discover available APIs, request
          access for your application, and read technical documentation.
        </Typography>

        <div className={classes.cardGrid}>
          <InfoCard
            title="Browse Available APIs"
            variant="gridItem"
            className={classes.gettingStartedCard}
          >
            <ExploreIcon className={classes.cardIcon} />
            <Typography variant="body2" className={classes.cardBody}>
              Explore the catalog of available APIs. View endpoints, rate limits, and data
              schemas before requesting access.
            </Typography>
            <LinkButton
              to="/api-docs"
              color="primary"
              variant="contained"
              className={classes.cardCta}
            >
              Browse APIs
            </LinkButton>
          </InfoCard>

          <InfoCard
            title="Request API Access"
            variant="gridItem"
            className={classes.gettingStartedCard}
          >
            <LockOpenIcon className={classes.cardIcon} />
            <Typography variant="body2" className={classes.cardBody}>
              Submit an access request for the APIs your application needs. A portal
              administrator will review and approve your request.
            </Typography>
            <LinkButton
              to="/marketplace"
              color="primary"
              variant="contained"
              className={classes.cardCta}
            >
              View API Marketplace
            </LinkButton>
          </InfoCard>

          <InfoCard
            title="Read Documentation"
            variant="gridItem"
            className={classes.gettingStartedCard}
          >
            <DescriptionIcon className={classes.cardIcon} />
            <Typography variant="body2" className={classes.cardBody}>
              Access technical guides, integration examples, and reference documentation
              for the available APIs.
            </Typography>
            <LinkButton
              to="/docs"
              color="primary"
              variant="contained"
              className={classes.cardCta}
            >
              Open Docs
            </LinkButton>
          </InfoCard>
        </div>
      </Content>
    </PageWithHeader>
  );
};
